/**
 * Página principal de gestión de roles
 * Sistema Klever Octopus - Betel Natural
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiX, FiAlertTriangle, FiSettings,
  FiEdit, FiCopy, FiTrash2, FiShield, FiChevronLeft, FiChevronRight, FiLoader
} from 'react-icons/fi';
import { useRoles } from '../../hooks';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../components/UI/Notifications/NotificationProvider';
import { ConfirmModal, Modal } from '../../components/UI/Modal';
import { Loading } from '../../components/UI/Loading';

const Roles = () => {
  const { hasPermission } = useAuth();
  const { showSuccess, showError } = useNotifications();
  const navigate = useNavigate();

  // Estados locales
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  // Hooks para datos
  const {
    roles,
    loading,
    error,
    pagination,
    deleteRole,
    duplicateRole,
    changePage,
    changeItemsPerPage,
    setError
  } = useRoles();

  // Verificar permisos
  // Permiso RBAC del módulo de roles (superusuario pasa por wildcard '*')
  const canManageRoles = hasPermission('admin.roles');
  const canCreate = canManageRoles;
  const canEdit = canManageRoles;
  const canDelete = canManageRoles;

  // Manejadores de eventos
  const handleCreateRole = () => {
    navigate('/system/admin/roles/nuevo');
  };

  const handleEditRole = (role) => {
    const roleIdentifier = role.value || role.id;
    navigate(`/system/admin/roles/${roleIdentifier}/editar`);
  };

  const handleDeleteRole = (role) => {
    setSelectedRole(role);
    setShowDeleteModal(true);
  };

  const handleDuplicateRole = (role) => {
    setSelectedRole(role);
    setShowDuplicateModal(true);
  };

  const confirmDelete = async () => {
    const roleIdentifier = selectedRole.value || selectedRole.id;

    if (!roleIdentifier) {
      showError('No se pudo identificar el rol a eliminar');
      return;
    }

    const result = await deleteRole(roleIdentifier);

    if (result.success) {
      showSuccess('Rol eliminado correctamente');
      setShowDeleteModal(false);
      setSelectedRole(null);
    } else {
      showError(result.message || 'Error al eliminar el rol');
    }
  };

  const confirmDuplicate = async (newRoleData) => {
    const result = await duplicateRole(selectedRole.id, newRoleData);

    if (result.success) {
      showSuccess('Rol duplicado correctamente');
      setShowDuplicateModal(false);
      setSelectedRole(null);
    } else {
      showError(result.message || 'Error al duplicar el rol');
    }
  };

  // Badge de nivel: el backend usa `level` (0-100); soporta `nivel_acceso` como respaldo
  const getRoleLevelBadge = (role) => {
    const level = role?.nivel_acceso ?? role?.level ?? 0;
    let cfg;
    if (level >= 90) cfg = { text: 'Admin', cls: 'level-admin' };
    else if (level >= 50) cfg = { text: 'Avanzado', cls: 'level-advanced' };
    else if (level >= 10) cfg = { text: 'Intermedio', cls: 'level-intermediate' };
    else if (level >= 1) cfg = { text: 'Básico', cls: 'level-basic' };
    else cfg = { text: 'N/A', cls: 'level-default' };

    return <span className={`level-badge ${cfg.cls}`}>{cfg.text}</span>;
  };

  if (loading && (!roles || roles.length === 0)) {
    return <Loading message="Cargando roles..." />;
  }

  return (
    <div className="roles-page">
      {/* Menú de acciones */}
      <div className="page-header-with-menu">
        <div className="action-menu">
          {canCreate && (
            <button className="menu-item menu-item--first" onClick={handleCreateRole}>
              + AGREGAR ROL
            </button>
          )}
        </div>
      </div>

      {/* Mensaje de error */}
      {error && (
        <div className="alert alert-error">
          <FiAlertTriangle />
          {error}
          <button className="btn btn-link" onClick={() => setError(null)}>
            <FiX />
          </button>
        </div>
      )}

      {/* Tabla de roles */}
      <div className="roles-table-container">
        <table className="roles-table">
          <thead>
            <tr>
              <th className="col-color"></th>
              <th>Nombre del Rol</th>
              <th>Descripción</th>
              <th className="col-center">Nivel</th>
              <th className="col-center">Usuarios</th>
              <th className="col-center">Permisos</th>
              <th className="col-center">Estado</th>
              <th className="col-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {roles && roles.map((role, index) => (
              <tr key={role.value || role.id || index}>
                {/* Indicador de color */}
                <td>
                  <div className="role-color-dot" style={{ backgroundColor: role.color || '#7B2D8E' }}></div>
                </td>

                {/* Nombre del rol */}
                <td>
                  <div className="role-name">
                    {role.display_name || role.name}
                    {role.is_system && (
                      <span className="system-tag">
                        <FiSettings size={10} />
                        Sistema
                      </span>
                    )}
                  </div>
                </td>

                {/* Descripción */}
                <td>
                  <span className="role-description">{role.description || 'Sin descripción'}</span>
                </td>

                {/* Nivel */}
                <td className="col-center">{getRoleLevelBadge(role)}</td>

                {/* Usuarios */}
                <td className="col-center">
                  <span className="role-users-count">{role.users_count || 0}</span>
                </td>

                {/* Permisos */}
                <td className="col-center">
                  <span className="role-perms-count">{role.permissions?.length || 0}</span>
                </td>

                {/* Estado */}
                <td className="col-center">
                  <span className={`status-pill ${role.is_active ? 'is-active' : 'is-inactive'}`}>
                    <span className="dot"></span>
                    {role.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </td>

                {/* Acciones */}
                <td className="col-center">
                  <div className="role-actions">
                    {canEdit && (
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => handleEditRole(role)}
                        title="Editar rol"
                      >
                        <FiEdit size={14} />
                        Editar
                      </button>
                    )}

                    {canCreate && (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleDuplicateRole(role)}
                        title="Duplicar rol"
                      >
                        <FiCopy size={14} />
                        Duplicar
                      </button>
                    )}

                    {canDelete && (
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeleteRole(role)}
                        title={
                          role.is_system
                            ? 'No se puede eliminar un rol del sistema'
                            : role.users_count > 0
                            ? `No se puede eliminar: ${role.users_count} usuario(s) asignado(s)`
                            : 'Eliminar rol'
                        }
                        disabled={role.is_system || role.users_count > 0}
                      >
                        <FiTrash2 size={14} />
                        Eliminar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Estado vacío */}
      {(!roles || roles.length === 0) && !loading && (
        <div className="empty-state">
          <FiShield />
          <h3>No se encontraron roles</h3>
          <p>No hay roles que coincidan con los filtros aplicados.</p>
          {canCreate && (
            <button className="btn btn-primary" onClick={handleCreateRole}>
              Crear primer rol
            </button>
          )}
        </div>
      )}

      {/* Paginación */}
      {pagination.totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Mostrando {roles?.length || 0} de {pagination.totalItems} roles
          </div>

          <div className="pagination-controls">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => changePage(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
            >
              <FiChevronLeft />
              Anterior
            </button>

            <span className="pagination-info">
              Página {pagination.currentPage} de {pagination.totalPages}
            </span>

            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => changePage(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
            >
              Siguiente
              <FiChevronRight />
            </button>
          </div>

          <select
            value={pagination.itemsPerPage}
            onChange={(e) => changeItemsPerPage(parseInt(e.target.value))}
            className="form-control pagination-select"
          >
            <option value={10}>10 por página</option>
            <option value={25}>25 por página</option>
            <option value={50}>50 por página</option>
          </select>
        </div>
      )}

      {/* Modales */}
      {showDeleteModal && selectedRole && (
        <ConfirmModal
          isOpen={showDeleteModal}
          title="Confirmar eliminación"
          message={`¿Estás seguro de que deseas eliminar el rol "${selectedRole.display_name || selectedRole.name}"?`}
          confirmText="Eliminar"
          confirmClass="btn-danger"
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedRole(null);
          }}
        />
      )}

      {showDuplicateModal && selectedRole && (
        <Modal
          title={`Duplicar Rol - ${selectedRole.display_name || selectedRole.name}`}
          isOpen={showDuplicateModal}
          onClose={() => {
            setShowDuplicateModal(false);
            setSelectedRole(null);
          }}
          size="medium"
        >
          <DuplicateRoleForm
            originalRole={selectedRole}
            onSubmit={confirmDuplicate}
            onCancel={() => {
              setShowDuplicateModal(false);
              setSelectedRole(null);
            }}
          />
        </Modal>
      )}

      {loading && (
        <div className="loading-overlay">
          <Loading message="Procesando..." />
        </div>
      )}
    </div>
  );
};

// Componente para duplicar rol
const DuplicateRoleForm = ({ originalRole, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: `${originalRole.name}_copy`,
    display_name: `${originalRole.display_name || originalRole.name} (Copia)`,
    description: `Copia de ${originalRole.display_name || originalRole.name}`,
    copy_permissions: true
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const permissionsCount = originalRole.permissions?.length || 0;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }
    if (!formData.display_name.trim()) {
      newErrors.display_name = 'El nombre para mostrar es requerido';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setSubmitting(true);
      await onSubmit(formData);
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="duplicate-role-form">
      <div className="form-group">
        <label htmlFor="name" className="required">Nombre del Rol (Interno)</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`form-control ${errors.name ? 'is-invalid' : ''}`}
          placeholder="nombre_rol"
        />
        <small className="form-text text-muted">Solo minúsculas y guiones bajos, sin espacios</small>
        {errors.name && (
          <div className="invalid-feedback">{errors.name}</div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="display_name" className="required">Nombre para Mostrar</label>
        <input
          type="text"
          id="display_name"
          name="display_name"
          value={formData.display_name}
          onChange={handleChange}
          className={`form-control ${errors.display_name ? 'is-invalid' : ''}`}
          placeholder="Nombre del Rol"
        />
        {errors.display_name && (
          <div className="invalid-feedback">{errors.display_name}</div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="description">Descripción</label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="form-control"
          rows="3"
          placeholder="Descripción del rol duplicado"
        />
      </div>

      <div className="form-group">
        <div className="form-check">
          <input
            type="checkbox"
            id="copy_permissions"
            name="copy_permissions"
            checked={formData.copy_permissions}
            onChange={handleChange}
            className="form-check-input"
          />
          <label htmlFor="copy_permissions" className="form-check-label">
            Copiar permisos del rol original
            <small className="help-text">
              {permissionsCount > 0
                ? `Se copiarán ${permissionsCount} permiso(s) de "${originalRole.display_name || originalRole.name}"`
                : 'El rol original no tiene permisos asignados'}
            </small>
          </label>
        </div>
      </div>

      <div className="modal-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancelar
        </button>

        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting}
        >
          {submitting ? (
            <>
              <FiLoader className="spin" />
              Duplicando...
            </>
          ) : (
            <>
              <FiCopy />
              Duplicar Rol
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default Roles;
